import React from 'react';
import Navbar from '../Components/Navbar';
import './Home.css';
import Footer from '../Components/Footer';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';

const comparisonData = [
    { name: 'Tensile Strength', Steel: 500, GFRP: 1000 },
    { name: 'Density', Steel: 7850, GFRP: 2100 },
    { name: 'Corrosion', Steel: 20, GFRP: 100 },
    { name: 'Lifespan', Steel: 25, GFRP: 100 }
];

const benefits = [
    { heading: 'Raw Materials', desc: 'Blitz rebars have 15% lower carbon footprint compared to traditional steel', image: '/raw.png' },
    { heading: 'Exceptional Strength', desc: 'Blitz rebars have twice the tensile strength of traditional steel rebars', image: '/strength.png' },
    { heading: 'LightWeight and Efficient', desc: '4x Ligher than steel, simplifying installation and reducing injury risks', image: '/lighter.png' },
    { heading: 'Manufacturing Process', desc: 'Utilizes minimum heat, water and energy and significantly reduces environmental impact', image: '/manufacture.png' },
    { heading: 'Transportation Efficiency', desc: 'Lighter than steel enabling more products per trucload and reducing fuel cost', image: '/delivery.png' },
    { heading: 'Usage and Longevity', desc: 'Increases asset lifecyle by up to 4 times and requires zero maintenance', image: '/useCase.png' },
    { heading: 'Recycling Potential', desc: 'Blitz composites can be crushed and reused as aggregates in concrete', image: '/recycle.png' },
    { heading: 'Chemical Resistance', desc: 'Highly resistance to aggressive chemicals and harsh environments', image: '/chemical.png' }
];

const testimonials = [
    {
        name: 'Project Director, Expressway Division',
        company: 'IRB Infrastructure Developers',
        role: 'Project Director',
        project: 'Six-lane coastal expressway structures',
        quote: 'BLITZ GFRP rebars eliminated our long-standing corrosion issues on coastal spans while keeping installation fast and safe for our teams.'
    },
    {
        name: 'Head – Metro & Urban Infra',
        company: 'Larsen and Toubro',
        role: 'Business Unit Head',
        project: 'Elevated metro and depot structures',
        quote: 'For complex metro packages, BLITZ delivered lightweight, non-corrosive reinforcement on schedule, helping us reduce dead load and long-term maintenance.'
    },
    {
        name: 'Infrastructure Delivery Lead',
        company: 'Tata Projects',
        role: 'Delivery Lead',
        project: 'Urban water and smart city assets',
        quote: 'BLITZ composite rebars were the most sustainable choice for our water and urban projects, combining long life with excellent technical support on site.'
    },
    {
        name: 'Chief Engineer – Tunnels & Marine',
        company: 'Afcons Infrastructure',
        role: 'Chief Engineer',
        project: 'Metro tunnels and marine corridors',
        quote: 'In aggressive marine and tunnel environments, BLITZ GFRP rebars performed exactly as promised and the team\'s responsiveness on design queries was outstanding.'
    }
];

const extendedTestimonials = [...testimonials, ...testimonials];

function Home() {
    const [currentIndex, setCurrentIndex] = React.useState(0);
    const [isTransitioning, setIsTransitioning] = React.useState(true);
    const [slideWidthPercent, setSlideWidthPercent] = React.useState(70);

    const videoRef = React.useRef(null);
    const [videoPlaying, setVideoPlaying] = React.useState(false);

    const totalSlides = testimonials.length;

    // Testimonials auto-slide
    React.useEffect(() => {
        const interval = setInterval(() => {
            setCurrentIndex(prev => prev + 1);
        }, 5000);
        return () => clearInterval(interval);
    }, []);

    // Responsive slider width
    React.useEffect(() => {
        const update = () => {
            const w = window.innerWidth;
            if (w <= 480) setSlideWidthPercent(100);
            else if (w <= 768) setSlideWidthPercent(80);
            else setSlideWidthPercent(70);
        };
        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, []);

    // Infinite loop reset
    React.useEffect(() => {
        if (currentIndex === totalSlides) {
            setTimeout(() => {
                setIsTransitioning(false);
                setCurrentIndex(0);
                requestAnimationFrame(() => setIsTransitioning(true));
            }, 600);
        }
    }, [currentIndex, totalSlides]);

    // ✅ Enhanced mobile-safe autoplay with multiple fallback strategies
    React.useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        // Force mute and inline playback
        video.muted = true;
        video.playsInline = true;
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.volume = 0;

        // Attempt 1: Try to play immediately
        const attemptPlay = () => {
            const playPromise = video.play();
            if (playPromise !== undefined) {
                playPromise
                    .then(() => {
                        setVideoPlaying(true);
                        console.log('Video autoplay successful');
                    })
                    .catch(error => {
                        console.log('Autoplay prevented, trying fallback:', error);
                        setVideoPlaying(false);
                    });
            }
        };

        // Attempt 2: Try after a small delay (helps with some browsers)
        const timeoutId = setTimeout(attemptPlay, 100);

        // Attempt 3: Play on any user interaction (fallback for strict policies)
        const playOnInteraction = () => {
            if (!videoPlaying && video.paused) {
                video.play()
                    .then(() => {
                        setVideoPlaying(true);
                        // Remove listeners after successful play
                        document.removeEventListener('touchstart', playOnInteraction);
                        document.removeEventListener('click', playOnInteraction);
                        document.removeEventListener('scroll', playOnInteraction);
                    })
                    .catch(() => { });
            }
        };

        document.addEventListener('touchstart', playOnInteraction, { once: true, passive: true });
        document.addEventListener('click', playOnInteraction, { once: true });
        document.addEventListener('scroll', playOnInteraction, { once: true, passive: true });

        // Cleanup
        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener('touchstart', playOnInteraction);
            document.removeEventListener('click', playOnInteraction);
            document.removeEventListener('scroll', playOnInteraction);
        };
    }, [videoPlaying]);

    return (
        <div className="home">
            <Navbar />

            {/* HERO */}
            <div className="hero-section">

                <video
                    ref={videoRef}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    poster=""
                    className="hero-background"
                    disablePictureInPicture
                    controlsList="nodownload nofullscreen noremoteplayback"
                    webkit-playsinline="true"
                >
                    <source
                        src="https://ninesouls-images.s3.ap-south-1.amazonaws.com/WhatsApp+Video+2026-01-03+at+02.39.29.mp4"
                        type="video/mp4"
                    />
                    Your browser does not support the video tag.
                </video>

                <div className="hero-overlay" />
                <div className="hero-content">
                    <p className="hero-tagline">The Future of Reinforcement</p>
                    <h1 className="hero-title">Stronger. Lighter.<br />Sustainable.</h1>
                    <p className="hero-subtitle">
                        India's leading GFRP rebar manufacturer — building infrastructure that lasts longer and leaves a lighter footprint.
                    </p>
                    <div className="hero-buttons">
                        <button className="hero-btn-primary">Get a Quote</button>
                        <button className="hero-btn-secondary">Learn More</button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className='main-content'>
                <div className="main-content-desc">
                    <div className="main-content-desc-item">
                        <p className="main-content-desc-item-heading">Enabling India's Net-Zero Mission</p>
                        <p className="main-content-desc-item-description">BLITZ Fiber Reinforced Polymer (FRP) rebars are revolutionizing sustainable construction with their exceptional corrosion resistance and lightweight properties. Our eco-friendly composite rebars reduce environmental impact while delivering superior performance in marine structures, bridge decks, and industrial facilities. By choosing BLITZ, you're investing in infrastructure that lasts longer, requires less maintenance, and supports India's commitment to sustainable development and carbon neutrality goals.</p>
                    </div>
                    <div className="main-content-desc-item-image-container">
                        <img src="/factory1edit.webp" className="main-content-desc-item-image"></img>
                    </div>
                </div>
            </div>
            <div className='whyUs'>
                <div className='sectionHeader'>
                    <p className='sectionHeading'>Why Choose Our GFRP rebars?</p>
                </div>
                <div className='benefits'>
                    {benefits.slice(0, 4).map((benefit, index) => {
                        return (
                            <div className='benefitCard' key={index}>
                                <img src={benefit.image} alt={benefit.heading} className='benefitCardImage' />
                                <p className='benefitCardHeading'>{benefit.heading}</p>
                                <p className='benefitCardDescription'>{benefit.desc}</p>
                            </div>
                        );
                    })}
                </div>
                <div className='benefits'>
                    {benefits.slice(4, 8).map((benefit, index) => {
                        return (
                            <div className='benefitCard' key={index}>
                                <img src={benefit.image} alt={benefit.heading} className='benefitCardImage' />
                                <p className='benefitCardHeading'>{benefit.heading}</p>
                                <p className='benefitCardDescription'>{benefit.desc}</p>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Full-width GFRP flyer hero image */}
            <div className="gfrpHeroSection">
                <img
                    src="/flyer.webp"
                    alt="Overview of BLITZ GFRP rebars benefits and applications"
                    className="gfrpHeroImage"
                />
            </div>

            {/* Custom GFRP Shapes Section */}
            <div className="customShapesSection">
                <div className="customShapesImageWrapper">
                    <img
                        src="/bentBars.webp"
                        alt="Customised GFRP bent bars and circular shapes"
                        className="customShapesImage"
                    />
                </div>
                <div className="customShapesContent">
                    <p className="customShapesTagline">Customised GFRP Shapes</p>
                    <h2 className="customShapesHeading">Precision-Engineered Shapes for Complex Geometry</h2>
                    <p className="customShapesDescription">
                        We manufacture customised GFRP shapes ranging from bent bars and stirrups to circular cages
                        and curved profiles, tailored to your structural requirements. Our flexible manufacturing
                        enables project-specific detailing while keeping all the benefits of corrosion-free, lightweight GFRP.
                    </p>
                    <ul className="customShapesList">
                        <li>Bent bars, hooks and stirrups for beams, slabs and precast elements</li>
                        <li>Circular and curved shapes for tanks, tunnels, marine and water-retaining structures</li>
                        <li>Project-specific shapes delivered ready-to-install to reduce site cutting and wastage</li>
                    </ul>
                </div>
            </div>

            {/* Sustainability Section */}
            <div className="sustainabilitySection">
                <div className="sustainabilityBgBlur sustainabilityBgBlur1"></div>
                <div className="sustainabilityBgBlur sustainabilityBgBlur2"></div>

                <div className="sustainabilityContainer">
                    <div className="sustainabilityContent">
                        <div className="sustainabilityTextContent">
                            <div className="sustainabilityBadge">
                                <img src="/recycle.png" alt="leaf" className="sustainabilityBadgeIcon" />
                                <span>Sustainable Construction</span>
                            </div>
                            <h2 className="sustainabilityHeading">Building a Greener Tomorrow</h2>
                            <p className="sustainabilityDescription">
                                Traditional steel production is carbon-intensive. GFRP manufacturing consumes
                                significantly less energy and releases fewer greenhouse gases. Plus, the longevity
                                of GFRP structures means less reconstruction and waste over time.
                            </p>
                            <div className="sustainabilityStats">
                                <div className="sustainabilityStat">
                                    <div className="sustainabilityStatValue">43%</div>
                                    <div className="sustainabilityStatLabel">Less Energy Consumption</div>
                                </div>
                                <div className="sustainabilityStat">
                                    <div className="sustainabilityStatValue">50%</div>
                                    <div className="sustainabilityStatLabel">Lower Carbon Emissions</div>
                                </div>
                            </div>
                        </div>

                        <div className="sustainabilityChartContainer">
                            <h3 className="sustainabilityChartTitle">Steel vs GFRP Performance</h3>
                            <div className="sustainabilityChart">
                                <ResponsiveContainer width="100%" height={260}>
                                    <BarChart
                                        data={comparisonData}
                                        margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                                        <YAxis tick={{ fontSize: 10 }} />
                                        <Tooltip />
                                        <Legend wrapperStyle={{ fontSize: 10 }} />
                                        <Bar dataKey="Steel" fill="#9ca3af" name="Steel (Grade 500)" />
                                        <Bar dataKey="GFRP" fill="#4D9B44" name="GFRP (High Grade)" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                            <p className="sustainabilityChartNote">*Illustrative comparison based on standard industry values</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="customerSection">
                <div className="sectionHeader">
                    <p className='sectionHeading'>Trusted by Many Happy Customers</p>
                    <p className='sectionDescription'>We partner with leading infrastructure developers, delivering durable, corrosion-free reinforcement for critical projects across India.</p>
                </div>
                <div className="customerCards">
                    <div className="customerCard">
                        <img src="/partner1.png" className='customerLogo' alt="Customer 1 logo" />
                        <p className="customerName">IRB</p>
                        <p className="customerDetail">Delivered GFRP rebars for highway and expressway structures, enhancing durability and reducing lifecycle maintenance.</p>
                    </div>
                    <div className="customerCard">
                        <img src="/partner2.png" className='customerLogo' alt="Customer 2 logo" />
                        <p className="customerName">Larsen and Toubro</p>
                        <p className="customerDetail">Supported large EPC infrastructure packages with lightweight, non-corrosive reinforcement for metro and industrial projects.</p>
                    </div>
                    <div className="customerCard">
                        <img src="/partner3.png" className='customerLogo' alt="Customer 3 logo" />
                        <p className="customerName">Tata Projects</p>
                        <p className="customerDetail">Provided composite rebars for urban infrastructure and water management assets, improving asset life and sustainability.</p>
                    </div>
                    <div className="customerCard">
                        <img src="/partner4.png" className='customerLogo' alt="Customer 4 logo" />
                        <p className="customerName">HCC</p>
                        <p className="customerDetail">Supplied high-performance GFRP reinforcement for complex bridges and hydro projects in challenging terrains.</p>
                    </div>
                    <div className="customerCard">
                        <img src="/partner5.png" className='customerLogo' alt="Customer 5 logo" />
                        <p className="customerName">Shapoorji Pallonji</p>
                        <p className="customerDetail">Enabled long-lasting reinforcement for marquee commercial and infrastructure landmarks across major cities.</p>
                    </div>
                    <div className="customerCard">
                        <img src="/partner6.png" className='customerLogo' alt="Customer 6 logo" />
                        <p className="customerName">Afcons</p>
                        <p className="customerDetail">Delivered corrosion-free rebars for metro tunnels, marine works and transport corridors in demanding environments.</p>
                    </div>
                </div>
            </div>
            <div className="testimonialsSection">
                <div className="sectionHeader">
                    <p className='sectionHeading'>What Our Clients Say</p>
                    <p className='sectionDescription'>Hear from project leaders who chose BLITZ GFRP rebars for their most demanding infrastructure works.</p>
                </div>
                <div className="testimonialSlider">
                    <div
                        className="testimonialTrack"
                        style={{
                            transform: `translateX(-${currentIndex * slideWidthPercent}%)`,
                            transition: isTransitioning ? 'transform 0.6s ease' : 'none'
                        }}
                    >
                        {extendedTestimonials.map((t, index) => {
                            const activeLogicalIndex = currentIndex % totalSlides;
                            const cardLogicalIndex = index % totalSlides;

                            return (
                                <div
                                    key={index}
                                    className={`testimonialCard ${cardLogicalIndex === activeLogicalIndex ? 'active' : 'inactive'}`}
                                >
                                    <p className="testimonialQuote">"{t.quote}"</p>
                                    <div className="testimonialMeta">
                                        <p className="testimonialName">{t.name}</p>
                                        <p className="testimonialRole">
                                            {t.role} • {t.company}
                                        </p>
                                        <p className="testimonialProject">{t.project}</p>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>
            <div className='aboveFooter'>
                <div className="sectionHeader">
                    <p className='sectionHeading'>Ready to Start Your Next Project?</p>
                    <p className='sectionDescription'>Contact us today to discuss your project and how we can help you achieve your goals.</p>
                </div>
                <div className="aboveFooterContent">
                    <button className='getQuote'>Get Quote</button>
                    <button className='download'>Download Brochure</button>
                </div>
            </div>
            <Footer />
        </div >
    )
}

export default Home;